import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getPackageById } from "../api/api";
import logo from "../Assets/lo.png";
import Loader from "../Loader/Loader";
import signature from "../Assets/kasim_sig.png";

const Invoice = () => {
  const { id } = useParams();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const invoiceRef = useRef();

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        const result = await getPackageById(id);
        setInvoiceData(result);
      } catch (err) {
        console.error("Error fetching invoice data:", err);
        setError("Failed to fetch invoice data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoiceData();
  }, [id]);

  const handleDownloadPDF = async () => {
    const invoiceElement = invoiceRef.current;
    const canvas = await html2canvas(invoiceElement);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

    // Add a new page for Terms & Conditions
    pdf.addPage();
    pdf.setFontSize(12);
    pdf.text("Digital Marketing Package – Terms & Conditions", 10, 10);
    
    const terms = [
      "Duration & Renewal: Minimum commitment of [3/6/12] months. Services auto-renew unless canceled 30 days in advance.",
      "Client Responsibilities: Provide necessary content, images, and access details on time. Delays may affect project timelines.",
      "Results Disclaimer: We apply best practices, but rankings, leads, and sales are influenced by various external factors. No guaranteed results.",
      "Content & Copyright: All content created remains our property unless agreed otherwise. Clients must ensure their provided materials do not infringe copyrights.",
      "Confidentiality: We respect your business data and do not share it with third parties without consent.",
      "Modifications: We may update services or terms with prior notice.",
      "Liability: We are not responsible for indirect losses, penalties, or third-party actions affecting results."
    ];

    terms.forEach((line, index) => {
      pdf.text(line, 10, 20 + (index * 10)); // Adjust the Y position for each line
    });

    pdf.save(`Invoice_${id}.pdf`);
  };

  if (loading) return <><Loader/></>;
  if (error) return <p className="text-red-500">{error}</p>;

  // Define the items array
  const items = [
    {
      name: "Posters",
      quantity: invoiceData.posterQuantity,
      amount: invoiceData.posterAmount,
    },
    {
      name: "Videos",
      quantity: invoiceData.videoQuantity,
      amount: invoiceData.videoAmount,
      additionalInfo: `(${invoiceData.influencerDuration})`,
    },
    {
      name: "Reviews",
      quantity: invoiceData.reviewQuantity || 0,
      amount: invoiceData.reviewAmount,
    },
    {
      name: "Influencer Ads",
      quantity: invoiceData.influencerQuantity,
      amount: invoiceData.influencerAmount,
    },
    {
      name: "Meta Ads",
      quantity: `Approx Reach: ${invoiceData.metaAdsReachMin} to ${invoiceData.metaAdsReachMax} users`,
      amount: invoiceData.metaAdsCost,
    },
    {
      name: "Google Ads",
      quantity: `Approx Clicks: ${invoiceData.googleAdsReachMin} to ${invoiceData.googleAdsReachMax} users`,
      amount: invoiceData.googleAdsCost,
    },
    {
      name: "YouTube Ads",
      quantity: `Approx Views: ${invoiceData.youtubeAdsReachMin} to ${invoiceData.youtubeAdsReachMax} users`,
      amount: invoiceData.youtubeAdsCost || 0,
    },
    {
      name: "Selected Platforms",
      quantity: invoiceData.selectedPlatforms?.join(", ") || "N/A",
      amount: invoiceData.publishingCost,
    },
  ];
  const filteredItems = items.filter(item => {
    return (
      item.quantity && item.amount && 
      item.quantity !== "N/A" && 
      item.amount !== "N/A" && 
      item.amount !== 0
    );
  });
  return (
    <div className="text-black max-w-3xl mx-auto  relative ">
      <div ref={invoiceRef} className="  relative z-10">
         <div className="border watermark p-6 h-[950px] "> 
         <div className="flex justify-between   flex-wrap items-center mb-6">
          <img src={logo} alt="Company Logo" className="h-16 w-auto" />
          <div className="text-right">
            <h2 className="text-2xl font-bold text-gray-800">Invoice</h2>
            <p className="text-gray-500"> <span className="text-gray-600">Invoice No: </span> {invoiceData.invoiceNo}</p>
            <span className="text-gray-500"> <span className="text-gray-600">Invoice Date: </span> 
           {invoiceData.createdAt.slice(0, 10).split('-').reverse().join('-')}</span>
          </div>
        </div>

        <hr className="my-4 border-gray-300" />

        <div className="mb-4 grid grid-cols-2 md:grid-cols-2 gap-4">
          <div>
            <p><strong>Name:</strong> {invoiceData.userData?.name || "N/A"}</p>
            <p><strong>Email:</strong> {invoiceData.userData?.email || "N/A"}</p>
            <p><strong>Mobile:</strong> {invoiceData.userData?.mobile || "N/A"}</p>
            <p><strong>GST No:</strong> {invoiceData.userData?.gstNo || "N/A"}</p>
          </div>
          <div>
            <p><strong>Business:</strong> {invoiceData.userData?.businessName || "N/A"}</p>
            <p><strong>Address:</strong> {invoiceData.userData?.address || "N/A"}</p>
          </div>
        </div>

        <hr className="my-4 border-gray-300" />

        <h3 className="text-lg font-semibold">Order Details</h3>
        <table className="w-full border border-gray-300 my-4">
          <thead className="bg-gray-200">
            <tr>
              <th className="border px-4 py-2">S.No</th>
              <th className="border px-4 py-2">Item</th>
              <th className="border px-4 py-2">Quantity</th>
              <th className="border px-4 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-2 text-center">{index + 1}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.name} {item.additionalInfo && <span className="text-sm">{item.additionalInfo}</span>}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.quantity}</td>
                  <td className="border border-gray-300 px-4 py-2">₹{item.amount}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="border border-gray-300 px-4 py-2 text-center">No items available</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex justify-between">
          <div className="p-4">
            <img src={signature} alt="kasim" className="w-28 h-auto" />
            <span className="font-medium text-sm">S. SYED KASIM, VFX.</span>
            <h1 className="text-md font-medium">Managing Director</h1>
          </div>
          <table className="w-3/6">
            <tbody>
              <tr><td>Monthly  </td><td className="text-end">₹{invoiceData?.totalCost || "N/A"}</td></tr>
             
              {/* <tr><td>Monthly</td><td className="text-end">₹{invoiceData.overallTotal || "N/A"}</td></tr> */}
              <tr>
  <td>{invoiceData.selectedPlan || "N/A"}</td>
  <td className="text-end">
    {invoiceData.beforeQuartlyDiscount 
      ? `₹${invoiceData.beforeQuartlyDiscount}` 
      : invoiceData.beforeHalfDiscount 
      ? `₹${invoiceData.beforeHalfDiscount}` 
      : invoiceData.beforeAnnualDiscount 
      ? `₹${invoiceData.beforeAnnualDiscount}` 
      : "N/A"}
  </td>
</tr>
              <tr><td className="mb-5">Discounted Price</td><td className="text-end mb-5">₹{invoiceData?.discountAmount || "N/A"}</td></tr>
              <tr><td className="mb-5">Total</td><td className="text-end mb-5">₹{invoiceData?.afterdiscount || "N/A"}</td></tr>

              <tr><td>IGST (18%)</td><td className="text-end">----</td></tr>
              
              <tr><td>CGST (9%)</td><td className="text-end">₹{invoiceData?.cgst.toFixed(2) || "N/A"}</td></tr>
              <tr><td>SGST (9%)</td><td className="text-end">₹{invoiceData?.gst.toFixed(2) || "N/A"}</td></tr>
              <tr>
                <td className="font-bold text-lg text-green-600">{invoiceData.selectedPlan || "Final"} Total</td>
                <td className="font-bold text-lg text-end text-green-600">
                  ₹ {invoiceData?.discountedPrice
                      ? invoiceData.discountedPrice.toLocaleString("en-IN", { minimumFractionDigits: 2 }) 
                      : "N/A"}
                </td> 
              </tr>
            </tbody>
          </table>
        </div>  
         </div>
      
      </div>

      {/* Terms & Conditions Section - This will be printed and included in the PDF */}
      <div className=" mt-24  border flex-col flex justify-between watermark p-6">
  {/* Payment Details Section */}
  <div className=" grid grid-cols-2">
  <div className="mb-6 p-4 ">
<h3 className="text-lg font-semibold text-gray-700 mb-2">Payment Details</h3>
<p><strong>Account Name:</strong> GREEN EMERALD AGENCY</p>
<p><strong>Bank:</strong> IDFC Bank, Vellore Branch</p>
<p><strong>Account Number:</strong> 54884400669</p>
<p><strong>IFSC Code:</strong> IDFB0080653</p>
<p><strong>GPay No:</strong> 9488440066</p>
</div>

{/* Contact Details Section */}
<div className="mb-6 p-4  ">
<h3 className="text-lg font-semibold text-gray-700 mb-2">Contact Information</h3>
<p><strong>Contact Person:</strong> Syed Kasim S</p>
<p><strong>Contact Number:</strong> 8566001166</p>

<p><strong>Company:</strong> GREEN EMERALD AGENCY</p>
<p><strong>Location:</strong> Vellore</p>
<p><strong>Designation:</strong> Managing Director</p>
</div>
  </div>
 
  <p className="mt-2 text-red-600 font-semibold text-center border mb-4 p-3">*After payment, please send a screenshot.*</p>

  {/* Terms & Conditions Section */}
  <div className="p-4 border ">
    <h3 className="text-lg font-semibold text-gray-700 mb-2 text-center">Terms & Conditions</h3>
    <p><strong>Duration & Renewal:</strong> Minimum commitment of [3/6/12] months. Services auto-renew unless canceled 30 days in advance.</p>
    <p><strong>Client Responsibilities:</strong> Provide necessary content, images, and access details on time. Delays may affect project timelines.</p>
    <p><strong>Results Disclaimer:</strong> We apply best practices, but rankings, leads, and sales are influenced by various external factors. No guaranteed results.</p>
    <p><strong>Content & Copyright:</strong> All content created remains our property unless agreed otherwise. Clients must ensure their provided materials do not infringe copyrights.</p>
    <p><strong>Confidentiality:</strong> We respect your business data and do not share it with third parties without consent.</p>
    <p><strong>Modifications:</strong> We may update services or terms with prior notice.</p>
    <p><strong>Liability:</strong> We are not responsible for indirect losses, penalties, or third-party actions affecting results.</p>
  </div>
</div>
      <div className="mt-6 flex justify-center space-x-4 no-print">
        <button onClick={() => window.print()} className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md">
          Print
        </button>
        <button onClick={handleDownloadPDF} className="px-4 py-2 bg-green-600 text-white rounded-lg shadow-md">
          Download PDF
        </button> 
        <Link to="/admin">
          <button className="px-4 py-2 bg-gray-600 text-white rounded-lg shadow-md">
            Back
          </button>
        </Link>

      </div>
    </div>  
  );
};

export default Invoice;